import {
    IInGameActionUIHandler,
    IInGameActionUIUpdater,
    InGameActionUI,
    IReinforcementsInfo,
    ITerritoryInfo,
    troopsMoveUIDomId} from "./common";

const sliderId = `${troopsMoveUIDomId}-slider`;
const numberInputId = `${troopsMoveUIDomId}-number-input`;
const submitButtonId = `${troopsMoveUIDomId}-submit`;

export function showTroopsMoveUI(
    from: ITerritoryInfo | IReinforcementsInfo,
    to: ITerritoryInfo,
    onSubmitCallback: (numTroops: number) => void,
    onCancelCallback: () => void,
    canClose = true,
    moveAtLeastOne = true,
): InGameActionUI {
    const handler = new TroopsMoveUIHandler(from, to, onSubmitCallback, onCancelCallback, moveAtLeastOne);
    const ui = new InGameActionUI(handler, from, to, canClose);
    ui.show();
    return ui;
}

class TroopsMoveUIHandler implements IInGameActionUIHandler {

    private readonly sourceIsTerritory: boolean;
    private readonly minTroopsToLeaveBehind: number;
    private uiUpdater: IInGameActionUIUpdater;
    private troopsBeingMoved: number;
    private minTroops: number;

    public constructor(
        private readonly from: ITerritoryInfo | IReinforcementsInfo,
        private readonly to: ITerritoryInfo,
        private readonly onSubmitCallback: (numTroops: number) => void,
        private readonly onCancelCallback: () => void,
        moveAtLeastOne: boolean,
    ) {
        this.sourceIsTerritory = ("territory" in from);
        this.minTroopsToLeaveBehind = this.sourceIsTerritory ? 1 : 0;
        this.troopsBeingMoved = from.numTroops - this.minTroopsToLeaveBehind;
        this.minTroops = moveAtLeastOne ? 1 : 0;
    }

    public buildControlTemplate(): string {
        return `<input type="range" id="${sliderId}">`;
    }

    public buildTopActionsTemplate(): string {
        return "";
    }

    public buildBottomActionsTemplate(): string {
        const submitString = this.sourceIsTerritory ? "Send" : "Place";
        return `
            <input class="number-input" id="${numberInputId}" type="number" value="0">
            <button id="${submitButtonId}">${submitString}</button>`;
    }

    public onShow(updater: IInGameActionUIUpdater): void {
        this.uiUpdater = updater;
        this.initializeInputs();
        this.initializeEvents();
        this.updateTroopCounts();
    }

    public onCancel(): void {
        this.onCancelCallback();
    }

    public onFromTroopsClicked(): void {
        this.trySetNumTroopsBeingMoved(this.troopsBeingMoved - 1);
    }

    public onToTroopsClicked(): void {
        this.trySetNumTroopsBeingMoved(this.troopsBeingMoved + 1);
    }

    private initializeInputs(): void {
        const slider = this.uiUpdater.element.find(`#${sliderId}`);
        slider.prop("min", this.minTroops);
        slider.prop("max", this.from.numTroops - this.minTroopsToLeaveBehind);
        slider.prop("step", 1);

        const numberInput = this.uiUpdater.element.find(`#${numberInputId}`);
        numberInput.prop("min", this.minTroops);
        numberInput.prop("max", this.from.numTroops - this.minTroopsToLeaveBehind);
        numberInput.prop("step", 1);
    }

    private initializeEvents(): void {
        this.uiUpdater.element.find(`#${submitButtonId}`).click(
            () => this.onSubmitButtonClicked());

        this.uiUpdater.element.find(`#${sliderId}`).on("input", () => {
            const numTroopsToMove = parseInt(
                this.uiUpdater.element.find(`#${sliderId}`).val() as string, 10);
            this.trySetNumTroopsBeingMoved(numTroopsToMove);
            return false; // suppress default event
        });
        this.uiUpdater.element.find(`#${numberInputId}`).on("input", () => {
            const numTroopsToMove = parseInt(
                this.uiUpdater.element.find(`#${numberInputId}`).val() as string, 10);
            this.trySetNumTroopsBeingMoved(numTroopsToMove);
            return false; // suppress default event
        });
    }

    private onSubmitButtonClicked(): boolean {
        this.onSubmitCallback(this.troopsBeingMoved);
        this.uiUpdater.close();
        return false; // suppress default event
    }

    private trySetNumTroopsBeingMoved(numTroops: number): boolean {
        if (numTroops < this.minTroops) {
            return false;
        }
        if (numTroops > this.from.numTroops - this.minTroopsToLeaveBehind) {
            return false;
        }

        this.setNumTroopsBeingMoved(numTroops);
        return true;
    }

    private setNumTroopsBeingMoved(numTroops: number): void {
        this.troopsBeingMoved = numTroops;
        this.updateTroopCounts();
    }

    private updateTroopCounts(): void {
        this.uiUpdater.setFromTroops(this.from.numTroops - this.troopsBeingMoved);
        this.uiUpdater.setToTroops(this.to.numTroops + this.troopsBeingMoved);

        this.uiUpdater.element.find(`#${sliderId}`).val(this.troopsBeingMoved);
        this.uiUpdater.element.find(`#${numberInputId}`).val(this.troopsBeingMoved);
    }
}
